// /services/sla-3d-printing/ (PrintingProcessPage.js). Code-owned since
// 2026-11-04; gatsby-node skips the Contentful service entry.
// GSC Aug–Oct 2026: 2 clicks, 5K impressions at position ~22. Queries kept in
// the title, H1 and H2s: "sla 3d printing service(s)", "sla 3d print service",
// "sla printing services", "resin 3d printing service".
// Facts: the SLA scope and resin list from truth.md and the live page; the
// published SLA equipment list (lib/public-facts.js); the large-format SLA
// platform of 600 × 600 mm (biggest-printers-guide.js). Prices are the
// general 3D printing ones; resin jobs are quoted per project.
const {
  pricing,
  photoRoot,
  reviewedDate,
  studios,
  delivery,
  files,
  noFile,
  hobby,
  quoteSteps,
  handoffStep,
} = require('./shared')
const printing = require('../printing-service')
const printingNyc = require('../printing-nyc')

const page = {
  path: '/services/sla-3d-printing/',
  crumb: 'SLA 3D printing',
  title: 'SLA 3D Printing Service | Resin Prototypes & Models',
  description: `SLA 3D printing service in New York: detailed resin prototypes, figurines and models in standard, Somos, clear, tough and castable resins, from about $${pricing.printFrom}.`,
  heading: 'SLA 3D Printing Service',
  eyebrow: 'Resin 3D printing service · Fine detail, smooth surfaces',
  answer: `PrintAWorld is an SLA 3D printing service in New York for detailed resin prototypes, figurines, miniatures, architectural models and presentation parts. We print in standard, Somos, clear, tough, castable and high-temperature resins on Formlabs and large-format SLA printers, then wash, cure, sand, paint or plate the parts if you need. From one part to a production run, with no setup fee: 3D printing starts around $${pricing.printFrom}, and we ship to all 50 states.`,
  hero: 'busts',
  faqHeading: 'What do people ask about SLA 3D printing?',
  quoteText:
    'Send the file, the size, the quantity and what the part is for. We’ll suggest a resin and reply with a price and a schedule.',
  reviewedDate,
}

const card = {
  label: 'Fine detail',
  title: 'SLA resin 3D printing',
  body: 'Smooth, detailed resin parts for figurines, models and prototypes.',
}

const photos = {
  busts: {
    ...printingNyc.photos.busts,
    title: 'Resin dragon and warrior bust',
  },
  horse: {
    url: `${photoRoot}/5BUh6SmMSlITH5CCSVC1PI/dbf952d68ef32a249f1d7bdb296684d4/west-world-3d-model-min.jpg`,
    width: 1500,
    height: 1001,
    alt: 'A white resin model of a horse with its skeleton and muscles showing',
    title: 'Anatomical horse model',
    caption: 'Fine ribs and joints in white resin, straight from the printer.',
  },
  architecture: {
    url: `${photoRoot}/5977okdVeABDOLjqyf0q2t/ee66aa36334a0536b3ecdf4e23060953/_DSC9279.jpg`,
    width: 1500,
    height: 1044,
    alt: 'A white Somos resin architectural model with a thin grid facade',
    title: 'Somos resin building model',
    caption: 'Thin facade grids printed in Somos resin.',
  },
  crab: {
    ...printing.photos.crab,
  },
  minis: {
    ...printing.photos.minis,
  },
  mask: {
    url: `${photoRoot}/yUb5L6XToGdPPBqMC6uEY/9ac3f860ee61c3a67a4e16a3f1f1aaea/Waterclear_Resin.jpg`,
    width: 7360,
    height: 4912,
    alt: 'A clear resin 3D printed face mask with vent slots',
    title: 'Clear face mask',
    caption: 'Printed in Somos WaterClear resin for a see-through part.',
  },
  heat: {
    url: `${photoRoot}/3tPje8LfFuBQBDu9SpvSbC/79e84a68076368e2b665196b65bb9fb9/High_Temp_resin.jpg`,
    width: 5199,
    height: 3560,
    alt: 'A translucent resin dragon head held in front of a flame',
    title: 'High-temperature resin',
    caption: 'A dragon head in high-temperature resin, held to a flame.',
  },
}

const intro = {
  eyebrow: 'Stereolithography',
  heading: 'What is SLA 3D printing?',
  paragraphs: [
    'SLA (stereolithography) cures liquid resin with ultraviolet light, one thin layer at a time. It gives the finest detail and smoothest surface of the common 3D printing processes, which is why it’s used for figurines, jewelry patterns, presentation prototypes and architectural models.',
    'Every SLA part is washed and cured after printing, and the supports are removed. We check your file first, pick the resin for what the part has to do, and can sand, paint or plate it so it’s ready to show.',
  ],
}

const glance = {
  heading: 'Our SLA 3D printing service at a glance',
  rows: [
    ['Studios', studios],
    ['Delivery', delivery],
    ['Production', 'In-house, in our New York studios'],
    [
      'Printers',
      'Formlabs Form 2 and Form 3, a UnionTech Lite600HD and our own PrintAWorld SLA-Pro 600',
    ],
    [
      'Largest size',
      'Large-format SLA has a 600 × 600 mm (23.6 × 23.6 in) build platform; bigger parts are printed in sections',
    ],
    [
      'Resins',
      'Standard, Somos, Somos WaterClear (clear), tough, castable (wax-filled) and high-temperature',
    ],
    ['Order size', 'One part to production runs'],
    ['Setup fee', 'None for 3D printing'],
    [
      'Price',
      `3D printing from about $${pricing.printFrom}; resin parts quoted by size, resin and finish`,
    ],
    [
      'Finishing',
      'Support removal, washing and curing; sanding, priming, painting or metal plating when quoted',
    ],
    ['Files', files],
    ['No file?', noFile],
  ],
}

const work = {
  heading: 'SLA resin 3D prints from our studio.',
  photos: ['horse', 'architecture', 'crab', 'minis', 'mask', 'heat'],
}

const before = [
  {
    id: 'resins',
    eyebrow: 'Materials',
    heading: 'Which SLA resins can you choose?',
    index: '02 / THE RESIN',
    cards: [
      {
        label: 'R1',
        title: 'Standard resin',
        body: 'Fine detail and a smooth surface for figurines, miniatures, models and display pieces.',
      },
      {
        label: 'R2',
        title: 'Somos resin',
        body: 'An industrial resin with crisp detail and a clean finish, for presentation prototypes and architectural models.',
      },
      {
        label: 'R3',
        title: 'Somos WaterClear',
        body: 'Clear, colorless parts that look like acrylic: lenses, light covers and see-through models.',
      },
      {
        label: 'R4',
        title: 'Tough resin',
        body: 'For prototypes that get handled, snapped together or tested, where standard resin would be too brittle.',
      },
      {
        label: 'R5',
        title: 'Castable resin',
        body: 'A wax-filled resin for casting patterns, such as jewelry. Check compatibility with your casting house.',
      },
      {
        label: 'R6',
        title: 'High-temperature resin',
        body: 'For parts that sit near heat, such as molds and fixtures. Tell us the temperature it has to take.',
      },
    ],
    note: [
      'Need nylon, ABS or metal instead? See our ',
      { to: '/materials/', label: '3D printing materials guide' },
      '.',
    ],
  },
]

const table = {
  id: 'compare',
  eyebrow: 'SLA vs FDM',
  heading: 'SLA vs FDM: which 3D printing process fits your part?',
  index: '03 / THE PROCESS',
  columns: ['', 'SLA resin', 'FDM plastic'],
  rows: [
    [
      'Detail',
      'Finest detail and sharp edges',
      'Good; layer lines show on small features',
    ],
    [
      'Surface',
      'Smooth, ready to sand and paint',
      'Visible layers unless sanded',
    ],
    [
      'Strength',
      'Rigid; tough resins for handled parts',
      'Tough plastics for working parts',
    ],
    [
      'Size',
      'Up to a 600 × 600 mm platform, larger in sections',
      'Large parts and big runs on our print farm',
    ],
    [
      'Best for',
      'Figurines, jewelry patterns, presentation models, clear parts',
      'Brackets, enclosures, fixtures, production runs',
    ],
  ],
  note: [
    'Not sure? Tell us what the part is for and we’ll pick the process. Big pieces go to ',
    {
      to: '/large-scale-3d-printing-service/',
      label: 'large-format 3D printing',
    },
    '; solid steel parts to ',
    { to: '/services/metal-3d-printing/', label: 'metal 3D printing' },
    '.',
  ],
}

const contrast = {
  id: 'finishing',
  eyebrow: 'Ready to show',
  heading: 'Can SLA prints be painted or plated?',
  paragraphs: [
    'Yes, and resin is the best base for it. Its smooth surface takes primer and paint cleanly, and it’s what we print before metal plating for chrome, gold or bronze looks.',
    'Finishing is quoted with the print: sanding and priming, painting in one color, brand colors or full color, or plating.',
  ],
  links: [
    {
      to: '/services/custom-3d-printing/',
      label: 'Painted and finished prints',
    },
    {
      to: '/full-color-3d-printing-service/',
      label: 'Full-color 3D printing',
    },
  ],
}

const fit = {
  eyebrow: 'Is it a fit?',
  heading: 'Is SLA resin 3D printing right for your project?',
  index: '04 / THE FIT',
  good: [
    'Presentation prototypes and product samples',
    'Figurines, miniatures, busts and collectibles',
    'Architectural models with fine detail',
    'Clear parts, casting patterns and heat-resistant fixtures',
    'Runs of the same resin part, with no setup fee',
    'Parts that will be painted or plated',
  ],
  other: [
    'Load-bearing or outdoor parts: FDM plastic or stainless steel usually suits them better, and we print both.',
    'The cheapest possible resin print: hobbyists and marketplaces compete on price.',
    hobby,
  ],
}

const pricingBlock = {
  heading: 'How much does SLA 3D printing cost?',
  index: '05 / THE PRICE',
  lead: {
    label: '3D printing starts around',
    value: `$${pricing.printFrom}`,
    note: 'For a simple print from a file that’s ready to print.',
  },
  rows: [
    ['Simple print from your file', `From about $${pricing.printFrom}`],
    ['Resin parts by size and resin', 'Quoted per project'],
    ['Sanding, painting, plating', 'Quoted per project'],
    ['3D modeling, if there’s no file', `From $${pricing.modelingFrom}`],
    ['Setup fee', 'None'],
    ['Shipping, next-day available', 'Quoted to your ZIP'],
  ],
  note: 'Part volume, resin, height on the build platform, quantity and finishing set the price. Your written quote confirms it.',
}

const steps = {
  heading: 'How our SLA 3D printing service works',
  index: '06 / THE PROCESS',
  items: [
    ...quoteSteps,
    {
      title: 'Print, wash and cure',
      body: 'We print in our New York studios, wash off uncured resin, post-cure the part and remove supports.',
    },
    {
      title: 'Finishing, if you want it',
      body: 'Sanding, priming, painting or metal plating, only when it’s in your quote.',
    },
    handoffStep,
  ],
}

const checklist = [
  'Your 3D file (STL, OBJ, STEP, STP or 3DM), or photos and a sketch',
  'Finished size, or the scale to print at',
  'How many you need',
  'Resin or finish, if you know it',
  'What the part is for: show, handling, fit, heat, casting',
  'Your delivery ZIP code and deadline',
]

const faqs = [
  {
    question: 'What is SLA 3D printing?',
    answer:
      'Stereolithography (SLA) is a 3D printing process that cures liquid photopolymer resin layer by layer with ultraviolet light. It gives fine detail and a smooth surface, so it suits figurines, presentation prototypes, jewelry patterns and architectural models.',
  },
  {
    question: 'How much does SLA 3D printing cost?',
    answer: `3D printing at PrintAWorld starts around $${pricing.printFrom} for a simple print from a ready file. Resin parts are quoted by size, resin, quantity and finishing, and there’s no setup fee. 3D modeling starts at $${pricing.modelingFrom} if you don’t have a file.`,
  },
  {
    question: 'Which SLA printers do you use?',
    answer:
      'Formlabs Form 2 and Form 3 machines, a UnionTech Lite600HD large-format printer and our own PrintAWorld SLA-Pro 600. The quote names the printer and resin that suit your part.',
  },
  {
    question: 'Are SLA prints strong?',
    answer:
      'Standard resin is rigid and can be brittle if it’s bent or dropped. Tough resin handles snapping and testing better. For load-bearing or outdoor parts, FDM plastic or stainless steel is usually a better choice; tell us how the part is used and we’ll recommend one.',
  },
  {
    question: 'How big can an SLA print be?',
    answer:
      'Our large-format SLA printers have a 600 × 600 mm (23.6 × 23.6 in) build platform. Bigger pieces are printed in sections and joined, which is how we make sculptures and props of 6 feet and more.',
  },
  {
    question: 'How long does SLA printing take?',
    answer:
      'Print time is only part of it: parts are washed, cured, cleaned up and finished too. Many jobs can be printed next day, and in New York we can deliver by courier. Same-day isn’t standard. Your written quote confirms the date.',
  },
  {
    question: 'Can you print clear parts with SLA?',
    answer:
      'Yes, in Somos WaterClear, a clear, colorless resin that looks like acrylic. It suits lenses, light covers and see-through models. Clarity depends on the shape and the finish, so tell us how clear it needs to be.',
  },
  {
    question: 'Do you only offer SLA 3D printing?',
    answer:
      'No. We also print on an in-house FDM print farm, print large-format pieces in sections and print solid stainless steel. If another process suits your part better, the quote says so.',
  },
]

module.exports = {
  page,
  card,
  photos,
  intro,
  glance,
  work,
  before,
  table,
  contrast,
  fit,
  pricing: pricingBlock,
  steps,
  checklist,
  faqs,
  schema: {
    name: 'SLA 3D Printing Service',
    serviceType: 'Stereolithography (SLA) resin 3D printing',
    description:
      'SLA resin 3D printing in New York for detailed prototypes, figurines, miniatures, architectural models and presentation parts, in standard, Somos, clear, tough, castable and high-temperature resins, with quoted finishing and shipping to all 50 states.',
    offer:
      '3D printing from about $30 for a simple print from a ready file; resin parts quoted by size, resin and finishing.',
    minPrice: pricing.printFrom,
  },
}
